import $i18n from '@alife/panda-i18n';
import React, {
  useCallback,
  useMemo,
  useRef,
  useState,
  useEffect,
  useImperativeHandle,
  forwardRef,
} from 'react';
import { v4 as uuidv4 } from 'uuid';
import { Input } from '@chatui/core';
import './index.scss';
import classNames from 'classnames';
import { CnDrawer, CnSearch, CnCheckbox, CnIcon, CnLoading, CnTooltip } from '@cainiaofe/cn-ui';
/** TODO: rich editor包含的wantEditor在webpack 5下编译有问题，整个模块先不使用 */
// import RichEditor from './rich-editor';
import Suggestions from './suggestions';
// import InputAddons from '../input-addons';
// import UploadButton from '../upload-button';
import htmlToPlainText from '@/utils/html-to-plain-text';
// import { AGENT_SERVICE_NAME, MODEL_SERVICE_NAME } from '@/utils/constant';
// import UploadList from '../upload-list';
// import { calculateCursorOffset, getRichContent } from './utils';

const FLUSH_ICON_URL =
  'https://gw.alicdn.com/imgextra/i2/O1CN017mojwN1eRas18Trd7_!!6000000003868-2-tps-32-33.png';
const DISABLE_FLUSH_ICON_URL =
  'https://gw.alicdn.com/imgextra/i2/O1CN014U9ZM81ayn7WmpagM_!!6000000003399-2-tps-32-33.png';

const ComposerInput = (props, ref) => {
  const {
    text: initialText = '',
    placeholder: oPlaceholder = $i18n.get({ id: 'PleaseEnter', dm: '请输入...' }),
    inputOptions,
    toolbar = [],
    onFocus,
    onBlur,
    onSend,
    onImageSend,
    onToolbarClick,
  } = props;

  const {
    hideInput,
    disableInput,
    disableInputHelp,
    modelServiceName,
    showModelServiceName,
    showFlushButton,
    onLoadMention,
    mentionDataSource = [],
    enableMention = false,
    mentionPrefix = '@',
    recordProcessing,
    minRows = 1,
    onClickSuggestion,
    onLoadSuggestion,
    suggestionDataSource = [],
    enableSuggestion = false,
    // addonData = [],
    // inputAddons = [],
    onInputAddonChange,
    ...otherInputOptions
  } = inputOptions;
  const [text, setText] = useState(initialText);
  const [textOnce, setTextOnce] = useState('');
  const inputRef = useRef(null);
  const [focused, setFocused] = useState(false);
  const [selectedMentions, setSelectedMentions] = useState([]);
  const [mentionVisible, setMentionVisible] = useState(false);
  // eslint-disable-next-line
  const [currentMentionPrefix, setCurrentMentionPrefix] = useState('');
  // eslint-disable-next-line
  const [currentInputPos, setCurrentInputPos] = useState(0);
  // eslint-disable-next-line
  const [editorInstance, setEditorInstance] = useState(null);
  const [mentionFocusKey, setMentionFocusKey] = useState('');
  const [selectedKeys, setSelectedKeys] = useState([]);
  const [isMultiple, setIsMultiple] = useState(false);
  // 文件上传列表
  const [fileList, setFileList] = useState([]);
  const menuRef = useRef(null);
  const addonRef = useRef(null);

  const innerSetText = useCallback(
    (_text) => {
      if (enableMention) {
        inputRef.current?.setText(_text);
      } else {
        setText(_text);
      }
    },
    [enableMention, inputRef],
  );

  useImperativeHandle(
    ref,
    () => ({
      setText: innerSetText,
      setFile: (file) => {
        setFileList([
          {
            name: file.name,
            key: file.key || uuidv4(),
            url: file.url,
            status: 'success',
          },
        ]);
      },
      setAddonValues: (values) => {
        if (addonRef.current) {
          addonRef.current.setValues(values);
        }
      },
    }),
    [innerSetText],
  );

  const handleInputFocus = useCallback(
    (e) => {
      setFocused(true);
      if (onFocus) {
        onFocus(e);
      }
    },
    [onFocus],
  );

  const handleInputBlur = useCallback(
    (e) => {
      setFocused(false);
      if (onBlur) {
        onBlur(e);
      }
    },
    [onBlur],
  );

  const handleTextChange = useCallback(
    (value) => {
      if (!enableMention && enableSuggestion && typeof onLoadSuggestion === 'function') {
        onLoadSuggestion({
          keyword: htmlToPlainText(value),
        });
      }
      setText(value);
    },
    [enableMention, enableSuggestion, onLoadSuggestion],
  );

  const send = useCallback(
    (e, from = 'enter') => {
      if (e && e.stopPropagation) e.stopPropagation();
      if (disableInput) return;
      if (text) {
        const plainText = htmlToPlainText(text);
        const richContent = '';
        // const richContent = enableMention
        //   ? getRichContent(plainText, mentionPrefix, selectedMentions)
        //   : '';
        setSelectedMentions([]);
        const options = enableMention ? { ...richContent } : {};
        const validFileList = fileList.filter((i) => i.status === 'success');
        if (validFileList.length > 0) {
          options.data = {
            url_list: validFileList.map((i) => i.url),
          };
          options.abilityType = 'IMG';
        }
        onSend(from, 'text', plainText, options);
        innerSetText('');
        setFileList([]);
      } else if (textOnce) {
        onSend(from, 'text', textOnce);
      }
      if (textOnce) {
        setTextOnce('');
      }
      if (focused) {
        inputRef.current.focus();
      }
    },
    [
      onSend,
      focused,
      text,
      innerSetText,
      textOnce,
      disableInput,
      selectedMentions,
      mentionPrefix,
      enableMention,
      fileList,
    ],
  );

  const handleMentionConfirm = useCallback(
    (key) => {
      const keyAry = Array.isArray(key) ? key : [key];
      const options = mentionDataSource.filter((i) => keyAry.includes(i.value));
      setSelectedMentions((cur) => {
        const newMentions = cur.slice(0);
        options.forEach((opt) => {
          const isOptionSelected = newMentions.some((item) => item.value === opt.value);
          if (!isOptionSelected) {
            newMentions.push(opt);
          }
        });
        return newMentions;
      });

      if (editorInstance) {
        editorInstance.restoreSelection(); // 恢复选区
        editorInstance.deleteBackward('character'); // 删除 '@'
        editorInstance.insertText(' '); // 插入一个空格，作为必要的分隔符
        options.forEach((i) => {
          const mentionNode = {
            type: 'mention', // 必须是 'mention'
            value: i.label, // 文本
            info: { value: i.value, prefix: currentMentionPrefix }, // 其他信息，自定义
            children: [{ text: '' }], // 必须有一个空 text 作为 children
          };

          editorInstance.insertNode(mentionNode); // 插入 mention
          editorInstance.move(1); // 移动光标
        });
      }
      editorInstance.insertText(' '); // 插入一个空格，作为必要的分隔符

      setMentionVisible(false);
      setSelectedKeys([]);
      setMentionFocusKey('');
      setIsMultiple(false);
    },
    [currentMentionPrefix, mentionDataSource, editorInstance],
  );

  const handleClickMultiple = useCallback(() => {
    if (!isMultiple) {
      setIsMultiple(true);
    } else {
      handleMentionConfirm(selectedKeys);
    }
  }, [isMultiple, handleMentionConfirm, selectedKeys]);

  const handleCheckChange = useCallback((key) => {
    setSelectedKeys((cur) => {
      const existIndex = cur.indexOf(key);
      const newKeys = cur.slice(0);
      if (existIndex >= 0) {
        newKeys.splice(existIndex, 1);
      } else {
        newKeys.push(key);
      }
      return newKeys;
    });
  }, []);

  const scrollMenuItem = useCallback(
    (index) => {
      const menuElement = menuRef.current;
      if (!menuElement) return;
      const menuItemElement = menuElement.childNodes?.[index];
      if (!menuItemElement || !menuItemElement.scrollIntoView) return;
      menuItemElement.scrollIntoView({ block: 'nearest' });
    },
    [menuRef.current],
  );

  const handleMentionKeyDown = useCallback(
    (e) => {
      if (e.key !== 'ArrowUp' && e.key !== 'ArrowDown' && e.key !== 'Enter') return;
      if (!mentionDataSource.length) return;
      if (!mentionFocusKey) {
        setMentionFocusKey(mentionDataSource[0].value);
      } else {
        const curIndex = mentionDataSource.map((i) => i.value).indexOf(mentionFocusKey);
        if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
          handleClickMultiple();
        } else if (e.key === 'ArrowDown') {
          const newIndex = curIndex + 1 > mentionDataSource.length - 1 ? 0 : curIndex + 1;
          setMentionFocusKey(mentionDataSource[newIndex].value);
          scrollMenuItem(newIndex);
        } else if (e.key === 'ArrowUp') {
          const newIndex = curIndex - 1 < 0 ? mentionDataSource.length - 1 : curIndex - 1;
          setMentionFocusKey(mentionDataSource[newIndex].value);
          scrollMenuItem(newIndex);
        } else if (e.key === 'Enter') {
          if (isMultiple) {
            handleCheckChange(mentionFocusKey);
          } else {
            handleMentionConfirm(mentionFocusKey);
          }
        }
      }
    },
    [
      isMultiple,
      mentionFocusKey,
      mentionDataSource,
      handleMentionConfirm,
      handleCheckChange,
      handleClickMultiple,
    ],
  );

  const handleInputKeyDown = useCallback(
    (e) => {
      if (disableInput) return;
      const mentionMenu = document.querySelector('.ant-mentions-dropdown');
      if (mentionVisible) {
        handleMentionKeyDown(e);
        e.preventDefault();
      } else if ((e.metaKey || e.ctrlKey) && e.keyCode === 13) {
        setText((cur) => `${cur}\n`);
      } else if (!e.shiftKey && !e.metaKey && !e.ctrlKey && e.keyCode === 13 && !mentionMenu) {
        send();
        e.preventDefault();
      }
    },
    [send, disableInput, mentionVisible, handleMentionKeyDown],
  );

  useEffect(() => {
    if (mentionDataSource.length > 0 && !mentionFocusKey) {
      setMentionFocusKey(mentionDataSource[0].value);
    }
  }, [mentionDataSource, mentionFocusKey]);

  const handleToolbarClick = useCallback(
    (item, e) => {
      if (onToolbarClick) {
        onToolbarClick(item, e);
      }
    },
    [onToolbarClick],
  );

  // const handleShowMention = useCallback(
  //   (editor, prefix) => {
  //     setEditorInstance(editor);
  //     setCurrentMentionPrefix(prefix);
  //     setMentionFocusKey('');
  //     const pos = calculateCursorOffset(editor);
  //     setCurrentInputPos(pos);
  //     if (typeof onLoadMention === 'function') {
  //       const plainText = htmlToPlainText(text);
  //       const richContent = getRichContent(plainText, mentionPrefix, selectedMentions);
  //       onLoadMention({
  //         keyword: '',
  //         prefix,
  //         ...richContent,
  //         text: plainText,
  //         position: pos > 0 ? pos - 1 : pos,
  //       });
  //     }
  //     setTimeout(() => {
  //       setMentionVisible(true);
  //     }, 100);
  //   },
  //   [text, onLoadMention, mentionPrefix, selectedMentions],
  // );

  // const handleHideMention = useCallback(() => {
  //   setMentionVisible(false);
  //   setSelectedKeys([]);
  //   setMentionFocusKey('');
  //   setIsMultiple(false);
  // }, []);

  const inputProps = {
    ...otherInputOptions,
    disabled: disableInput,
    value: text,
    inputRef,
    placeholder: disableInput && disableInputHelp ? disableInputHelp : oPlaceholder,
    onFocus: handleInputFocus,
    onBlur: handleInputBlur,
    onKeyDown: handleInputKeyDown,
    onChange: handleTextChange,
    onImageSend,
  };

  const plaintext = useMemo(() => {
    return htmlToPlainText(text);
  }, [text]);

  const isSimple = useMemo(() => {
    const isMobile = /Mobile/.test(navigator.userAgent);
    return isMobile && !!focused;
  }, [focused]);

  const handleSearch = useCallback(
    (value) => {
      if (typeof onLoadMention === 'function') {
        const plainText = htmlToPlainText(text);
        // const richContent = getRichContent(plainText, mentionPrefix, selectedMentions);
        const richContent = {};
        onLoadMention({
          keyword: value,
          prefix: currentMentionPrefix,
          ...richContent,
          text: plainText,
          position: currentInputPos,
        });
      }
    },
    [text, currentMentionPrefix, onLoadMention, currentInputPos, mentionPrefix, selectedMentions],
  );

  if (hideInput) {
    return null;
  }

  return (
    <div
      className={classNames('InputContainer', 'chatai-ui-composer-input', {
        disabled: disableInput,
        simple: isSimple,
      })}
    >
      <div className="inputMain">
        {showFlushButton ? (
          <CnTooltip
            align="t"
            trigger={
              <div
                className="flushButton"
                onClick={() => {
                  if (disableInput) return;
                  handleToolbarClick({
                    type: 'flush',
                  });
                }}
              >
                <img
                  className="flushIcon"
                  src={disableInput ? DISABLE_FLUSH_ICON_URL : FLUSH_ICON_URL}
                />
              </div>
            }
          >
            {$i18n.get({ id: 'EmptyDialog', dm: '清空对话' })}
          </CnTooltip>
        ) : null}
        <div
          className="inputBlock"
          onClick={() => {
            inputRef.current?.focus();
          }}
        >
          {enableSuggestion ? (
            <Suggestions
              keyword={plaintext}
              dataSource={suggestionDataSource}
              onClick={onClickSuggestion}
            />
          ) : null}
          {/* <UploadList
            fileList={fileList}
            onDelete={(key) => {
              setFileList((cur) => cur.filter((i) => i.key !== key));
            }}
          /> */}
          <div className="inputContent">
            <div className="inputRow">
              {recordProcessing ? (
                <CnLoading size="small">
                  <div className="loading" />
                </CnLoading>
              ) : null}
              {/* {enableMention ? (
                <RichEditor
                  {...inputProps}
                  ref={inputRef}
                  minRows={minRows}
                  onShowMention={handleShowMention}
                  onHideMention={handleHideMention}
                  mentionPrefix={mentionPrefix}
                />
              ) : ( */}
              <Input
                className="input"
                ref={inputRef}
                minRows={minRows}
                maxRows={10}
                // rows={(text || '').split('\n') || 1}
                enterKeyHint="send"
                autoSize
                {...inputProps}
              />
              {/* )} */}
            </div>
            <div
              className={classNames(!isSimple || !disableInput ? 'actions' : '', {
                disabled: disableInput,
              })}
            >
              {isSimple ? null : (
                <div className="tools">
                  {toolbar.map((item) => {
                    return (
                      <div
                        className="toolButton"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (disableInput) return;
                          handleToolbarClick(item, e);
                        }}
                      >
                        <CnIcon type={item.icon} size={20} />
                      </div>
                    );
                  })}
                </div>
              )}
              <div className={classNames('rightActions', { disabled: disableInput })}>
                {/* TODO: modelServiceName */}
                {/* {showModelServiceName &&
                !isSimple &&
                modelServiceName !== MODEL_SERVICE_NAME &&
                modelServiceName !== AGENT_SERVICE_NAME ? (
                  <div className={styles.modelServiceName}>{modelServiceName}</div>
                ) : null} */}
                <div
                  className="sendButton"
                  onClick={(e) => {
                    send(e, 'click');
                  }}
                >
                  <img
                    className="sendIcon"
                    src="https://gw.alicdn.com/imgextra/i4/O1CN01UlE41V1nkfupJJYDB_!!6000000005128-2-tps-28-28.png"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* TODO: input addons */}
      {/* <InputAddons
        ref={addonRef}
        addonData={addonData}
        addons={inputAddons}
        onChange={onInputAddonChange}
      /> */}
      <CnDrawer
        visible={mentionVisible}
        title={$i18n.get({ id: 'PleaseSelectAnObjectToMention', dm: '请选择要提及的对象' })}
        noCard
        placement="bottom"
        onClose={() => {
          setMentionVisible(false);
          setSelectedKeys([]);
          setMentionFocusKey('');
          setIsMultiple(false);
        }}
        className="mentionDrawer"
      >
        <CnSearch
          placeholder={$i18n.get({
            id: 'PleaseEnterAKeywordAndPressEnter_534852949',
            dm: '请输入关键词后按Enter搜索',
          })}
          shape="simple"
          onSearch={handleSearch}
          className="search"
        />

        {mentionDataSource?.length ? (
          <div className="tip" onClick={handleClickMultiple}>
            {!isMultiple
              ? $i18n.get({
                  id: 'EnterMultiselectModeCommandEnter',
                  dm: '进入多选模式（Command+Enter)',
                })
              : $i18n.get({ id: 'CompleteCommandEnter', dm: '完成（Command+Enter）' })}
          </div>
        ) : null}
        <div className="menu" ref={menuRef}>
          {!mentionDataSource?.length ? (
            <div className="empty">{$i18n.get({ id: 'TERM.NoData', dm: '暂无数据' })}</div>
          ) : null}
          {mentionDataSource.map((i) => (
            <div
              className={classNames({
                menuItem: true,
                focused: i.value === mentionFocusKey,
              })}
            >
              {isMultiple ? (
                <CnCheckbox
                  checked={selectedKeys.includes(i.value)}
                  onChange={(checked) => {
                    handleCheckChange(i.value, checked);
                  }}
                >
                  {i.label}
                </CnCheckbox>
              ) : (
                <div
                  onClick={() => {
                    handleMentionConfirm(i.value);
                  }}
                >
                  {i.label}
                </div>
              )}
            </div>
          ))}
        </div>
      </CnDrawer>
    </div>
  );
};

export default forwardRef(ComposerInput);
